import { useId } from 'react';
import { ReferenceArea, type ReferenceAreaProps, ReferenceLine } from 'recharts';

import type { ReferenceAreaSpec, ReferenceLineSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { CHART_SHADE, chartElementColor, fillOpacity, mantineColorVar } from '#core/lib/formatters/colors.ts';

import { dashArray } from '../cell.ts';
import type { ChartOrientation } from './chartOrientation.ts';

export function ChartReferenceMarkers({
  o,
  lines,
  areas,
  cited,
}: {
  o: ChartOrientation;
  lines: readonly ReferenceLineSpec[];
  areas: readonly ReferenceAreaSpec[];
  /** Labels of markers cited by insights — those render emphasized. */
  cited?: ReadonlySet<string>;
}) {
  const isCited = (label?: string) => label !== undefined && (cited?.has(label) ?? false);
  const markerLabel = (value: string | undefined, fill: string, emphasized: boolean) => ({
    value,
    fill,
    position: 'top' as const,
    fontWeight: emphasized ? 700 : 500,
    fontSize: 12,
  });
  return (
    <>
      {lines.map((rl, i) => {
        const coords = o.refLineCoords(rl.axis, o.normalizeRef(rl.axis, rl.value));
        const color = mantineColorVar(chartElementColor(rl.color, i), CHART_SHADE.ref);
        const emphasized = isCited(rl.label);
        return (
          <ReferenceLine
            {...coords}
            ifOverflow="visible"
            key={`rl-${rl.axis}-${rl.value}-${rl.label ?? ''}`}
            label={markerLabel(rl.label, color, emphasized)}
            stroke={color}
            strokeDasharray={dashArray(rl.style)}
            strokeWidth={emphasized ? 2 : 1}
          />
        );
      })}
      {areas.map((ra, i) => {
        const coords = o.refAreaCoords(ra.axis, o.normalizeRef(ra.axis, ra.start), o.normalizeRef(ra.axis, ra.end));
        const color = mantineColorVar(chartElementColor(ra.color, i), CHART_SHADE.ref);
        const emphasized = isCited(ra.label);
        return (
          <FilledArea
            {...coords}
            fillOpacity={emphasized ? fillOpacity.strong : fillOpacity.refArea}
            ifOverflow="visible"
            key={`ra-${ra.axis}-${ra.start}-${ra.end}-${ra.label ?? ''}`}
            label={markerLabel(ra.label, color, emphasized)}
            patternFill={color}
          />
        );
      })}
    </>
  );
}

function FilledArea({ patternFill, ...props }: ReferenceAreaProps & { patternFill: string }) {
  const stripeId = useId();
  return (
    <>
      <ReferenceArea {...props} fill={`url(#${stripeId})`} />
      <defs>
        <pattern height="4" id={stripeId} patternTransform="rotate(45)" patternUnits="userSpaceOnUse" width="4">
          <rect fill={patternFill} height="4" width="1" />
        </pattern>
      </defs>
    </>
  );
}
